/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import LRUCache from 'lru-cache';

import { decimalPlaces, formatNumber, pad } from '../utils/number';
import { dateFormatter } from '../utils/dateFormatter';
import type {
  RowGetter, CellDataGetter, CellRenderer, OnRowClick,
} from '../utils/dataTypes';
import { getLabel } from '../utils/i18n';

import { FlexBox } from './FlexBox';
import EmptyResult from './EmptyResult';
import ScrollableTable from './ScrollableTable';
import LoadingShade from './LoadingShade';
import SelfOtherPairsToggler from './SelfOtherPairsToggler';
import ProTableFilters from './ProTableFilters';
import { setPriceAndAmount as setPriceAndAmountAction, fetchUserTradesData, resetUserTradesPagination } from '../actions/actionsSet/order';
import { setUserTradesDataPeriod, setDataIsLoading } from '../actions/load';
import { PAIR_WHITE_LIST } from '../network/GetTradePageInitialProps';

const DEFAULT_ROW = 200;
const DEFAULT_WIDTH = 270;
const DEFAULT_HEIGHT = 340;
const DEFAULT_TRADES_LINES = 80;
const ROW_HEIGHT = 20;
const ALIGN_RIGHT = { 'justifyContent': 'flex-end' };
const TABLE_ROW_STYLE = { 'cursor': 'pointer' };

// performance optimisation - cache `allTrades` from `mapStateToProps`
const allTradesCache = new LRUCache({
  max: 5,
});

const AlignRight = styled.span`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
`;
const AlignRightGray = styled(AlignRight)`
 color:${({ theme }) => theme.fontColor2};
`;

const AlignRightGray2 = styled(AlignRight)`
 color:${({ theme }) => theme.fontColor5};
`;
const Tabs = styled.ul`
  display: flex;
  position: absolute;
  right: 0;
  top: -1.8em;
`;

const Tab = styled.li`
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme, active }) => active ? theme.buttonColorActive : theme.fontColor};
  cursor: ${({ active }) => !active ? 'pointer' : 'auto'};
  display: flex;
  height: 18px;
  line-height: 17px;
  padding: 0 10px;
  &:not(:first-child) {
    border-left: none;
  }
`;

/* TODO: Avoid being complicated here. */
const Container = styled(FlexBox)`
  flex: 1 0 100%;
  position: relative;
  overflow: ${({ omitTab, pro }) => omitTab && !pro ? 'hidden' : 'visible'}; /* no dashes overflow on pro. */
`;

const Wrapper = styled.div`
  position: relative;
  height: ${({ pro }) => pro ? 'calc(100% - 2.5em)' : '100%'};
  width: 100%;
  display: flex;
  justify-content: center;
`;

const RowRenderer = styled(FlexBox)`
  border-bottom: 1px solid ${({ theme }) => theme.border};
  cursor: pointer;
`;

type TabProps = {
  i18n: Object,
  tabClick?: Function,
  view?: string,
};

const TabComponent = ({ i18n, tabClick, view }: TabProps) => (
  <Tabs>
    <Tab onClick={tabClick} data-view="market" active={view === 'market'}>{getLabel('exchange-Trades-Market', i18n, 'Market') }</Tab>
    <Tab onClick={tabClick} data-view="yours" active={view === 'yours'}>{getLabel('exchange-Trades-Yours', i18n, 'Yours')}</Tab>
  </Tabs>
);

TabComponent.defaultProps = {
  tabClick: () => {},
  view: 'market',
};

const GreenAmount = styled.span` color: ${({ theme }) => theme.up}; `;
const RedAmount = styled.span` color: ${({ theme }) => theme.down}; `;

const EMPTY = [];

const mapStateToProps = ({
  tickers,
  trades,
  userTrades,
  userTradesWithoutSymbol,
  isLoggedIn,
  isDataLoading,
  locale,
  userTradesPagination,
  userTradesDataPeriod,
  userTradesStart,
  userTradesEnd,
  marginStatus,
}, { symbol }) => {
  if (PAIR_WHITE_LIST.find((p) => p.pair === symbol)) {
    return { trades: EMPTY, marginStatus };
  }
  if (!trades || !trades[symbol]) {
    if (!isLoggedIn && (!userTrades || !userTrades[symbol])) {
      return {
        trades: EMPTY, userTrades: EMPTY, allUserTrades: EMPTY, marginStatus,
      };
    }
    return { trades: EMPTY, marginStatus };
  }
  const { tickSize, minTrade } = tickers[symbol] || {};
  const tickSizeDecimals = decimalPlaces(tickSize);
  const minTradeDecimals = decimalPlaces(minTrade);
  return {
    trades: trades[symbol],
    userTrades,
    userTradesWithoutSymbol,
    allUserTrades: allTradesCache.get(userTrades) ||
      Object.keys(userTrades).reduce((list, s) => {
        list.push(...userTrades[s]);
        return list;
      }, []),
    minTradeDecimals,
    tickSizeDecimals,
    isLoggedIn,
    isDataLoading: isDataLoading.trades,
    locale,
    userTradesPagination,
    userTradesDataPeriod,
    userTradesStart,
    userTradesEnd,
    isDataLoadingTrades: isDataLoading.userTrades,
    marginStatus,
  };
};

type Props = {
  i18n: Object,
  isLoggedIn: boolean,
  omitTab: boolean,
  symbol: string,
  locale?: string,
  trades?: Array<Object>,
  userTrades?: Array<Object>,
  allUserTrades?: Array<Object>,
  minTradeDecimals: number,
  tickSizeDecimals: number,
  defaultWidth: number,
  defaultHeight: number,
  rowHeight: number,
  isDataLoading: boolean,
  pro: boolean,
  setPriceAndAmount?: Function,
  fetchUserTradesData: Function,
  setDataIsLoading: Function,
  isDataLoadingTrades: boolean,
  setUserTradesDataPeriod?: Function,
  resetUserTradesPagination: Function,
  userTradesPagination?: Object,
  userTradesDataPeriod?: string,
  userTradesStart: number,
  userTradesEnd: number,
  userTradesWithoutSymbol?: Array<Object>,
  marginStatus: boolean,
}

type State = {
  tradeHistoryView: string,
  hideOtherPairs: boolean,
  start: number,
}

type TableProps = { columns?: React.Node }

export default
@connect(mapStateToProps, {
  setPriceAndAmount: setPriceAndAmountAction,
  fetchUserTradesData,
  setUserTradesDataPeriod,
  setDataIsLoading,
  resetUserTradesPagination,
})
class Trades extends React.Component<Props, State> {
  _table = React.createRef();

  static defaultProps = {
    i18n: {},
    locale: '',
    isLoggedIn: false,
    omitTab: false,
    minTradeDecimals: 8,
    tickSizeDecimals: 8,
    trades: [],
    userTrades: [],
    allUserTrades: [],
    defaultWidth: DEFAULT_WIDTH,
    defaultHeight: DEFAULT_HEIGHT,
    rowHeight: ROW_HEIGHT,
    isDataLoading: true,
    pro: false,
    setPriceAndAmount: () => {},
    fetchUserTradesData: () => {},
    setDataIsLoading: () => {},
    isDataLoadingTrades: false,
    setUserTradesDataPeriod: () => {},
    resetUserTradesPagination: () => {},
    userTradesPagination: {},
    userTradesDataPeriod: '1d',
    userTradesStart: -1,
    userTradesEnd: -1,
    userTradesWithoutSymbol: [],
    marginStatus: false,
  }

  state = {
    tradeHistoryView: 'market',
    hideOtherPairs: false,
    start: -1,
  }

  componentDidMount() {
    /* eslint-disable */
      const {locale,isLoggedIn,fetchUserTradesData,userTradesStart:start, userTradesEnd:end }=this.props;
      this.setState({ hideOtherPairs: JSON.parse(sessionStorage.getItem('tradesHideOtherPairs') || 'false') });
      if(isLoggedIn && start>0 && end>0) {
        fetchUserTradesData({
          startTime: start,
          endTime: end,
          page: 1,
          rows: DEFAULT_ROW,
        }, locale, false)
      }
  }

  static getDerivedStateFromProps(props: Props) {
    // update `allTradesCache`, see `mapStateToProps`
    allTradesCache.set(props.userTrades, props.allUserTrades);
    return {};
  }

  async componentDidUpdate(prevProps:Props): any {
    const {pro,marginStatus,resetUserTradesPagination}=this.props;
    if(pro && prevProps.marginStatus !== marginStatus) {
      // await resetUserTradesPagination();
      this._loadData(false)
    }
    }

  _getTrades = () => {
    const {
      trades, userTradesWithoutSymbol, allUserTrades, pro, symbol,
    } = this.props;
    if (!userTradesWithoutSymbol) return EMPTY;
    const { start, tradeHistoryView, hideOtherPairs } = this.state;
    const isUsingMarketTrades = tradeHistoryView === 'market' && !pro;
    let targetTrades;
    if (!pro) {
      targetTrades = tradeHistoryView === 'market' ? trades : allUserTrades;
    } else {
      targetTrades = userTradesWithoutSymbol;
    }
    if (!targetTrades) return EMPTY;
    return targetTrades.filter(item => {
      if (start !== -1 && item.T < start) return false; // local time filter
      if (isUsingMarketTrades) return true; // non-pro, trades are for this symbol anyway.
      if (hideOtherPairs || !pro) return item.s === symbol;
      return true;
    });
  }

  _rowGetter = ({ index }: RowGetter) => {
    const targetTrades = this._getTrades();
    if (index + 1 > targetTrades.length) return ['–', '–', '–'];
    return targetTrades[index];
  }

  _rowRenderer = (props: TableProps) => <RowRenderer {...props}>{props.columns}</RowRenderer>

  _noRowsRenderer = (props: Props) =>
    <EmptyResult {...props} message={this.props.i18n['exchange-common-noResult']} pro={this.props.pro} />

  _cellGetterPrice = ({ rowData }: CellRenderer) => Array.isArray(rowData) ? rowData : rowData.p

  _isSell = (rowData: Object) => {
    if (Array.isArray(rowData)) return rowData;

    const isMarketView = this.state.tradeHistoryView === 'market';

    return (isMarketView && rowData.m) || rowData.d === 'SELL';
  }

  _cellRendererPrice = ({ rowData, cellData }: CellRenderer) => {
    if (Array.isArray(rowData)) return <span>–</span>;
    const { tickSizeDecimals, pro } = this.props;
    const formatted = pro ? formatNumber(cellData,8).replace(/0*$/, '0') : formatNumber(cellData,tickSizeDecimals);

    if (this._isSell(rowData)) {
      return <RedAmount>{formatted}</RedAmount>;
    }

    return <GreenAmount>{formatted}</GreenAmount>;
  }

  _cellGetterPair = ({ rowData }: CellDataGetter) => rowData

  _cellRendererPair = ({ cellData }: CellRenderer) => `${cellData.ba}/${cellData.qa}`

  _cellGetterType = ({ rowData }: CellDataGetter) => Array.isArray(rowData) ? rowData : rowData.d

  _cellRendererType = ({ cellData }: CellRenderer) =>
    cellData === 'BUY' ? <GreenAmount>{this.props.i18n[cellData]}</GreenAmount> :
    <RedAmount>{this.props.i18n[cellData]}</RedAmount>

  _cellGetterAmount = ({ rowData }: CellDataGetter) => Array.isArray(rowData) ? rowData : rowData.q

  _cellRendererAmount = ({ rowData, cellData }: CellRenderer) => {
    if (Array.isArray(rowData)) return <AlignRightGray2>–</AlignRightGray2>;
    const { minTradeDecimals, pro } = this.props;
    const formatted = formatNumber(cellData, minTradeDecimals);
    return pro ? formatted : (<AlignRightGray2>{formatted}</AlignRightGray2>);
  }

  _cellGetterTime = ({ rowData }: CellDataGetter) => Array.isArray(rowData) ? rowData : rowData.T

  _cellRendererTime = ({ rowData, cellData }: CellRenderer) => {
    if (Array.isArray(rowData)) return (<AlignRightGray>–</AlignRightGray>);
    const d = new Date(cellData);
    const short = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    return this.props.pro ? dateFormatter(d) : (<AlignRightGray>{short}</AlignRightGray>);
  };

  _cellGetterTotal = ({ rowData }: CellDataGetter) => Array.isArray(rowData) ? rowData : rowData.tq

  _cellRendererTotal = ({ rowData, cellData }: CellRenderer) => {
    const { tickSizeDecimals } = this.props;
    return `${formatNumber(cellData, tickSizeDecimals)} ${rowData.qa}`;
  }

  _cellGetterFee = ({ rowData }: CellDataGetter) => Array.isArray(rowData) ? rowData : rowData.f

  _cellRendererFee = ({ rowData, cellData }: CellRenderer) => `${formatNumber(cellData, 8)} ${rowData.fa}`

  _onTabClick = (ev: MouseEvent) => {
    if (!ev || !ev.currentTarget || (!(ev.currentTarget instanceof HTMLLIElement))) return;
    const tradeHistoryView = ev.currentTarget.dataset.view;
    this.setState({ tradeHistoryView });
  }

  _onCheckboxClick = () => {
    sessionStorage.setItem('tradesHideOtherPairs', JSON.stringify(!this.state.hideOtherPairs));
    this.setState({ hideOtherPairs: !this.state.hideOtherPairs });
  }

  _onRowClick = ({ rowData }: OnRowClick) => {
    const { setPriceAndAmount, symbol } = this.props;
    if (!setPriceAndAmount) return;
    const side = this._isSell(rowData) ? 'SELL' : 'BUY';
    setPriceAndAmount({
      side,
      price: rowData.p,
      symbol,
    });
  }

  _onFilterChange = (start: number, end: number, bool: boolean, period: string) => {
    if (!this.props.setUserTradesDataPeriod) return;
    bool ? this.props.setUserTradesDataPeriod({period,start,end}) : '';
    const { locale } = this.props;
    this.props.setDataIsLoading({ key: 'userTrades', isLoading: true });
    if (!this.props.fetchUserTradesData) return;
    this.props.fetchUserTradesData({
      startTime: start,
      endTime: end,
      page: 1,
      rows: DEFAULT_ROW,
    }, locale, false).then(() => {
      this.props.setDataIsLoading({ key: 'userTrades', isLoading: false });
    });
  };

  _loadData = (bottom:boolean) => {
    const {
      locale,
      userTradesPagination,
    } = this.props;
    if (!userTradesPagination || !this.props.fetchUserTradesData) return;
    const { startTime, endTime, pages } = userTradesPagination;
    const page = bottom ? userTradesPagination.page + 1 : userTradesPagination.page;
    if ( bottom && page > pages) return;
    this.props.fetchUserTradesData({
      startTime,
      endTime,
      page,
      rows: DEFAULT_ROW,
    }, locale, bottom);
  };

  render() {
    const {
      i18n,
      trades,
      allUserTrades,
      isLoggedIn,
      omitTab,
      defaultWidth,
      defaultHeight,
      rowHeight,
      isDataLoading,
      symbol,
      pro,
      userTradesDataPeriod,
      isDataLoadingTrades,
      marginStatus,
      locale,
    } = this.props;
    const { tradeHistoryView } = this.state;
    const rowCount = () => {
      if (!trades || !allUserTrades) return 0;
      return Math.max(tradeHistoryView === 'market' && !pro ? DEFAULT_TRADES_LINES : 0, tradeHistoryView === 'market' && !pro ? trades.length : allUserTrades.length);
    };
    return (
      <Container bg="theme" pro={pro} omitTab={omitTab}>
        { isLoggedIn && !omitTab ?
          <TabComponent i18n={i18n} tabClick={this._onTabClick} view={tradeHistoryView} /> : null}
        { pro ?
          <React.Fragment>
            <SelfOtherPairsToggler
              id="trade-toggler"
              i18n={i18n}
              pro={pro}
              readMore={true}
              marginStatus={marginStatus}
              onClick={this._onCheckboxClick}
              checked={this.state.hideOtherPairs}
              textKey={''}
              locale={locale}
            />
            <ProTableFilters
              symbol={symbol}
              period={userTradesDataPeriod}
              onFilterChange={this._onFilterChange}
            />
          </React.Fragment> : null}
        <Wrapper pro={pro}>
          { pro ?
            <React.Fragment>
              <ScrollableTable
                defaultWidth={defaultWidth}
                defaultHeight={defaultHeight}
                headerHeight={30}
                rowHeight={rowHeight}
                rowCount={this._getTrades().length}
                rowGetter={this._rowGetter}
                rowRenderer={this._rowRenderer}
                onRowClick={this._onRowClick}
                scrollable={(defaultHeight / ROW_HEIGHT) < this._getTrades().length}
                noRowsRenderer={this._noRowsRenderer}
                nonce={typeof window !== 'undefined' && window.__NONCE__}
                rowStyle={TABLE_ROW_STYLE}
                isBottom={this._loadData}
              >
                <ScrollableTable.Column
                  label={getLabel('exchange-Trades-Dealtime', i18n, 'Time')}
                  dataKey="T"
                  width={400}
                  cellDataGetter={this._cellGetterTime}
                  cellRenderer={this._cellRendererTime}
                />
                <ScrollableTable.Column
                  label={getLabel('exchange-Trades-Market', i18n, 'Market')}
                  dataKey="s"
                  width={200}
                  cellDataGetter={this._cellGetterPair}
                  cellRenderer={this._cellRendererPair}
                />
                <ScrollableTable.Column
                  label={getLabel('exchange-Trades-TradeSide', i18n, 'Type')}
                  dataKey="s"
                  width={100}
                  cellDataGetter={this._cellGetterType}
                  cellRenderer={this._cellRendererType}
                />
                <ScrollableTable.Column
                  label={getLabel('exchange-Trades-TradePrice', i18n, 'Price')}
                  dataKey="p"
                  width={300}
                  cellDataGetter={this._cellGetterPrice}
                  cellRenderer={this._cellRendererPrice}
                />
                <ScrollableTable.Column
                  label={getLabel('exchange-TradeOrdersTable-filled', i18n, 'Filled')}
                  dataKey="q"
                  width={300}
                  cellDataGetter={this._cellGetterAmount}
                  cellRenderer={this._cellRendererAmount}
                />
                <ScrollableTable.Column
                  label={getLabel('exchange-TradeOrdersTable-Fee', i18n, 'Fee')}
                  dataKey="q"
                  width={300}
                  cellDataGetter={this._cellGetterFee}
                  cellRenderer={this._cellRendererFee}
                />
                <ScrollableTable.Column
                  label={getLabel('exchange-TradeOrdersTable-TradeTotal', i18n, 'Total')}
                  dataKey="q"
                  width={300}
                  cellDataGetter={this._cellGetterTotal}
                  cellRenderer={this._cellRendererTotal}
                />
              </ScrollableTable>
              <LoadingShade hidden={!isDataLoadingTrades} />
            </React.Fragment>
            :
            <React.Fragment>
              <LoadingShade hidden={!isDataLoading} />
              <ScrollableTable
                defaultWidth={DEFAULT_WIDTH}
                defaultHeight={defaultHeight}
                headerHeight={pro ? 30 : 0}
                rowHeight={18}
                rowCount={rowCount()}
                rowGetter={this._rowGetter}
                onRowClick={this._onRowClick}
                scrollable={(defaultHeight / ROW_HEIGHT) < this._getTrades().length}
                noRowsRenderer={this._noRowsRenderer}
                nonce={typeof window !== 'undefined' && window.__NONCE__}
                isHeaderBgWhite={true}
                rowStyle={TABLE_ROW_STYLE}
              >
                <ScrollableTable.Column
                  label={getLabel('exchange-Trades-TradePrice', i18n, 'Price')}
                  dataKey="p"
                  width={1000}
                  cellDataGetter={this._cellGetterPrice}
                  cellRenderer={this._cellRendererPrice}
                />
                <ScrollableTable.Column
                  label={getLabel('exchange-common-amount', i18n, 'Amount')}
                  dataKey="q"
                  width={1000}
                  cellDataGetter={this._cellGetterAmount}
                  cellRenderer={this._cellRendererAmount}
                  style={ALIGN_RIGHT}
                />
                <ScrollableTable.Column
                  label={getLabel('excchange-common-date', i18n, 'Time')}
                  dataKey="T"
                  width={1000}
                  cellDataGetter={this._cellGetterTime}
                  cellRenderer={this._cellRendererTime}
                  style={ALIGN_RIGHT}
                />
              </ScrollableTable>
            </React.Fragment>
          }
        </Wrapper>
      </Container>
    );
  }
}
